import { onBeforeUnmount } from '@nuxtjs/composition-api';
import axios, { CancelToken, CancelTokenSource } from 'axios';

const cancelTokenSourceMap: Map<symbol, CancelTokenSource> = new Map();

type CancellableOptions = {
  /** コンポーネントがUnmountしたときにキャンセルを実施するか */
  cancelWhenUnmount?: boolean;
};

// FIXME: もしかして、グローバルに値を保持する必要ないかも
/** CancelTokenを参照する。不明なSymbolでもCancelTokenを生成して返す */
export const getCancelToken = (symbol = Symbol('unknown')): CancelToken => {
  const { token } = cancelTokenSourceMap.get(symbol) || axios.CancelToken.source();

  return token;
};

export const useCancellable = <Key extends string>(keys: [Key, ...Key[]], options: CancellableOptions = {}) => {
  const { cancelWhenUnmount = true } = options;
  const symbols: Record<Key, symbol> = Object.fromEntries(keys.map((key) => [key, Symbol(key)])) as any;

  /**
   * キーからキャンセルトークンを参照する
   * @param key キー
   */
  const token = (key: Key): CancelToken => {
    const { [key]: symbol } = symbols;

    return getCancelToken(symbol);
  };

  /**
   * リクエストをキャンセルしてトークンを再生成する
   * @param reason キャンセルを実施する理由
   */
  const cancel = (keys: [Key, ...Key[]], reason?: string): void => {
    keys.forEach((key) => {
      const { [key]: symbol } = symbols;
      const cancelTokenSource = cancelTokenSourceMap.get(symbol);

      if (cancelTokenSource) {
        let message = `#${key}`;

        if (reason) {
          message += ` (${reason})`;
        }

        cancelTokenSource.cancel(message);
      }

      // キャンセルしたら再発行する
      if (symbol) {
        cancelTokenSourceMap.set(symbol, axios.CancelToken.source());
      }
    });
  };

  /**
   * インスタンスのすべてのリクエストをキャンセルする
   * @param reason キャンセルを実施する理由
   */
  const cancelAll = (reason?: string) => {
    cancel(keys, reason);
  };

  /**
   * この Composable から登録したシンボルを削除する
   */
  const destroy = () => {
    Object.values<symbol>(symbols).forEach((symbol) => {
      cancelTokenSourceMap.delete(symbol);
    });
  };

  // サーバーサイドでは画面遷移や多重リクエストなどのキャンセルしたいケースがないので
  // クライアントのみでCancelTokenを発行する
  if (process.client) {
    Object.values<symbol>(symbols).forEach((symbol) => {
      cancelTokenSourceMap.set(symbol, axios.CancelToken.source());
    });
  }

  // Unmount時にリクエスト中のメッセージを削除する
  onBeforeUnmount(() => {
    if (cancelWhenUnmount) {
      cancelAll('Unmounted');
    }

    destroy();
  });

  return {
    token,
    cancel,
    cancelAll,
  };
};
